// Centralized brand constants
export const BRAND = {
  name: "CPS Consulting LLC",
  short: "CPS Consulting",
  tagline: "Comprehensive Professional Solutions",
  email: "info@cpsconsulting.com",
  founder: "J. Jey Smith",
};

export const HERO_SLIDES = [
  {
    title: "From Vision to Implementation",
    subtitle: "Consortium Building for Complex Developments",
    description:
      "We convene capital, technical expertise, and stakeholders to move large-scale infrastructure, energy, and real estate projects from concept to execution.",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=2000&q=80",
    accent: "Consortium Leaders",
  },
  {
    title: "Energy Transitions, Engineered",
    subtitle: "Renewables, Grid & Power Project Advisory",
    description:
      "End-to-end project controls for wind, solar, transmission, and energy storage developments at scale.",
    image:
      "https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=2000&q=80",
    accent: "Energy",
  },
  {
    title: "Real Estate That Performs",
    subtitle: "Disciplined Process Over Simple Networking",
    description:
      "Site readiness, procurement, contractor alignment, and capital structuring for mixed-use and commercial developments.",
    image:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=2000&q=80",
    accent: "Strategy & People",
  },
  {
    title: "The Consortium Builder",
    subtitle: "Bridging Vision and Execution",
    description:
      "An umbrella platform connecting investors, operators, and technical partners around a shared, executable plan.",
    image:
      "https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=2000&q=80",
    accent: "Multistakeholder",
  },
];

export const SECTORS = [
  {
    name: "Energy",
    blurb:
      "Renewables, grid modernization, storage & traditional power assets — structured for bankability.",
    image:
      "https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=1200&q=80",
    icon: "Zap",
  },
  {
    name: "Real Estate",
    blurb:
      "Mixed-use, commercial, hospitality & residential developments brought from site to certificate of occupancy.",
    image:
      "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1200&q=80",
    icon: "Building2",
  },
  {
    name: "Infrastructure",
    blurb:
      "Transport, water, ports & utilities — public, private and PPP structures with rigorous controls.",
    image:
      "https://images.unsplash.com/photo-1473445730015-841f29a9490b?auto=format&fit=crop&w=1200&q=80",
    icon: "Network",
  },
  {
    name: "Construction Materials",
    blurb:
      "Cement, aggregates, steel and specialty inputs — supply alignment with project schedules.",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=80",
    icon: "HardHat",
  },
  {
    name: "Commodities",
    blurb:
      "Sourcing, offtake, logistics and trade-finance structuring for industrial-scale flows.",
    image:
      "https://images.unsplash.com/photo-1605000797499-95a51c5269ae?auto=format&fit=crop&w=1200&q=80",
    icon: "Package",
  },
  {
    name: "Media & Communications",
    blurb:
      "Narrative, stakeholder communications and brand positioning for capital-intensive ventures.",
    image:
      "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80",
    icon: "Radio",
  },
];

export const SERVICES = [
  {
    title: "Business Development Strategy",
    icon: "Compass",
    description:
      "Market entry, partner mapping and growth roadmaps anchored in disciplined diligence.",
    bullets: [
      "Opportunity screening",
      "Partner & channel mapping",
      "Go-to-market playbooks",
    ],
  },
  {
    title: "Consortium Building",
    icon: "Users",
    description:
      "We assemble the right operators, investors and technical partners around a single execution plan.",
    bullets: [
      "Stakeholder alignment",
      "Capital syndication",
      "Joint-venture structuring",
    ],
  },
  {
    title: "Project Management",
    icon: "ClipboardList",
    description:
      "Scheduling, budgeting and risk tracking that keep complex programs on the rails.",
    bullets: [
      "Master schedule & budget",
      "Risk & change controls",
      "Executive reporting",
    ],
  },
  {
    title: "Construction Management",
    icon: "HardHat",
    description:
      "Site readiness, procurement and contractor alignment from mobilization to handover.",
    bullets: [
      "Procurement strategy",
      "Contractor oversight",
      "Quality & safety controls",
    ],
  },
  {
    title: "Investor & Capital Alignment",
    icon: "TrendingUp",
    description:
      "Structuring, investor materials and engagement to match the right capital to the right project.",
    bullets: [
      "Capital stack design",
      "Investor narratives",
      "Diligence support",
    ],
  },
  {
    title: "Media & Communications",
    icon: "Megaphone",
    description:
      "Stakeholder communications and positioning for capital-intensive, multi-party ventures.",
    bullets: [
      "Stakeholder messaging",
      "Press & public affairs",
      "Brand architecture",
    ],
  },
];

export const STATS = [
  { value: 120, suffix: "+", label: "Stakeholders convened" },
  { value: 6, suffix: "", label: "Sectors served" },
  { value: 25, suffix: "+", label: "Years of leadership" },
  { value: 1.2, suffix: "B+", prefix: "$", decimals: 1, label: "Capital aligned" },
];

export const SECTOR_OPTIONS = [
  "Energy",
  "Real Estate",
  "Infrastructure",
  "Construction Materials",
  "Commodities",
  "Media & Communications",
  "Other",
];

export const STATUS_OPTIONS = [
  "Concept / Idea",
  "Pre-feasibility",
  "Feasibility complete",
  "Ready for funding",
  "Execution phase",
  "Stalled / Needs restructuring",
];

export const CHALLENGE_OPTIONS = [
  "Capital needs",
  "Stakeholder alignment",
  "Technical gaps",
  "Schedule & risk",
  "Procurement / supply chain",
  "Regulatory / permitting",
  "Communications & narrative",
];

export const BUDGET_OPTIONS = [
  "< $1M",
  "$1M – $10M",
  "$10M – $50M",
  "$50M – $250M",
  "$250M – $1B",
  "> $1B",
];

export const TIMELINE_OPTIONS = [
  "0 – 3 months",
  "3 – 6 months",
  "6 – 12 months",
  "1 – 3 years",
  "3+ years",
];
